import {
  adherenceFor,
  coverageDays,
  defaultBucketSize,
  isQuantitative,
  type Aggregate,
  type BucketSize,
  type Entry,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { Select } from '../../components/Field.js'
import { OverlayChart } from '../../charts/OverlayChart.js'
import { TrackChart } from '../../charts/TrackChart.js'
import { trackColour } from '../../charts/colours.js'
import { CoverageHeatmap } from '../../charts/CoverageHeatmap.js'
import { RangeControl } from '../../range/RangeControl.js'
import { useRange } from '../../range/RangeProvider.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Insights.module.css'
import { useSavedOverlays } from './useSavedOverlays.js'

const BUCKET_SIZES: readonly BucketSize[] = ['hour', 'day', 'week', 'month']
const AGGREGATES: readonly Aggregate[] = ['mean', 'median', 'min', 'max', 'count', 'sum']

/**
 * Charts over a date range (T-403, T-404).
 *
 * Nothing on this screen is calculated here. Series, buckets, statistics,
 * trends and normalisation all come from `@anytracker/core`, so the numbers on
 * screen are the same ones the PDF report will print and the same ones the
 * native app will show.
 */
export function InsightsScreen() {
  const { repos, clock } = useServices()
  const { resolved, ready } = useRange()

  const [tracks, setTracks] = useState<Track[] | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  /*
   * `?track=` names the chart to open on (T-809).
   *
   * The same idiom the timeline already uses for `?tag=`, and it does two jobs:
   * it lets the post-save confirmation land on a chart of the track that was
   * just recorded rather than whatever was last viewed, and it makes a
   * particular chart a link somebody can keep.
   */
  const [params] = useSearchParams()
  const requestedId = params.get('track')
  const [pickedId, setPickedId] = useState<string | null>(requestedId)
  const [overlayIds, setOverlayIds] = useState<string[]>([])
  const [sizeChoice, setSizeChoice] = useState<BucketSize | 'auto'>('auto')
  const [aggregate, setAggregate] = useState<Aggregate>('mean')

  const span = useMemo(
    () => ({ from: resolved.fromEpochMs, to: resolved.toEpochMs }),
    [resolved.fromEpochMs, resolved.toEpochMs],
  )

  const reload = useCallback(async () => {
    // Nothing is queried until the range is real. "All time" resolves to today
    // until the first and last entry have been read, and a query against that
    // returns almost nothing — which would be shown, and then replaced.
    if (!ready) {
      return
    }
    const [allTracks, found] = await Promise.all([
      // Archived included: a track retired last month still has three months of
      // history worth charting, and hiding it makes that history unreachable.
      repos.tracks.list({ includeArchived: true }),
      repos.entries.find({
        range: { fromEpochMs: span.from, toEpochMs: span.to },
      }),
    ])
    setTracks(allTracks)
    setEntries(found)
  }, [repos, ready, span.from, span.to])

  useEffect(() => {
    void reload()
  }, [reload])

  const { saved, save, remove } = useSavedOverlays()

  /**
   * Derived every render rather than latched by an effect.
   *
   * An effect that picks a default once runs before the entries arrive, lands
   * on whichever track happens to be first, and never reconsiders — so the
   * screen opens on an empty chart while another track has months of history.
   * Deriving it means the default follows the data, and a deliberate choice
   * still wins because `pickedId` takes precedence.
   */
  const preferredId = useMemo(() => {
    const measured = new Set(
      entries.flatMap((entry) => entry.measurements.map((m) => m.trackId)),
    )
    return (
      (tracks ?? []).find((track) => measured.has(track.id))?.id ?? tracks?.[0]?.id ?? ''
    )
  }, [tracks, entries])

  const selectedId = pickedId ?? preferredId
  const size = sizeChoice === 'auto' ? defaultBucketSize(resolved) : sizeChoice
  const selected = tracks?.find((track) => track.id === selectedId)
  const overlayTracks = (tracks ?? []).filter((track) => overlayIds.includes(track.id))
  const deviceZone = clock.timeZone()

  const adherence = useMemo(() => adherenceFor(entries, resolved), [entries, resolved])
  const coverage = useMemo(() => coverageDays(entries, resolved), [entries, resolved])

  return (
    <>
      {/*
        The heading and the range control render before the data arrives. They
        do not depend on it, and a screen that replaces itself wholesale a tick
        after opening reads as a page load rather than as a page.
      */}
      <div className={styles['header']}>
        <h1>Insights</h1>
      </div>

      <RangeControl empty={tracks !== null && entries.length === 0} />

      {tracks === null ? (
        <p>Loading…</p>
      ) : tracks.length === 0 ? (
        <p className={styles['nothing']}>
          Define a track and record against it, and its history appears here.
        </p>
      ) : (
        <>
          {/*
            Coverage, above the charts rather than buried under them. A chart of
            four days out of ninety looks exactly like a chart of ninety, and
            nothing else on the page says which one is being read.
          */}
          {/*
            The grid says where the gaps fell, which is a different fact from
            how many there were — one bad fortnight and a steady drizzle produce
            the same count and look nothing alike. The sentence above it carries
            the numbers, because the grid is hidden from assistive technology.
          */}
          <div data-testid="coverage">
            <CoverageHeatmap
              days={coverage}
              caption={`Logged on ${String(adherence.loggedDays)} of ${String(adherence.totalDays)} days${
                adherence.longestGapDays > 0
                  ? ` · longest gap ${String(adherence.longestGapDays)} ${adherence.longestGapDays === 1 ? 'day' : 'days'}`
                  : ''
              }`}
            />
          </div>

          <div className={styles['controls']}>
            <Select
              label="Track"
              value={selectedId}
              onChange={(event) => {
                setPickedId(event.target.value)
              }}
            >
              {tracks.map((track) => (
                <option key={track.id} value={track.id}>
                  {track.name}
                  {track.archived ? ' (archived)' : ''}
                </option>
              ))}
            </Select>
            <Select
              label="Group by"
              value={sizeChoice}
              onChange={(event) => {
                setSizeChoice(event.target.value as BucketSize | 'auto')
              }}
            >
              <option value="auto">Automatic ({size})</option>
              {BUCKET_SIZES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            {/*
              Only where there is something to summarise.

              A track that cannot produce a mean — a marker, an unordered enum —
              was still offered "Summarise with: Mean", which is the screen
              contradicting itself about the data it is drawing. Those are
              counted, and a count has no aggregate to choose.
            */}
            {selected !== undefined && isQuantitative(selected.kind) && (
              <Select
                label="Summarise with"
                value={aggregate}
                onChange={(event) => {
                  setAggregate(event.target.value as Aggregate)
                }}
              >
                {AGGREGATES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </div>

          {selected !== undefined && (
            <TrackChart
              track={selected}
              entries={entries}
              span={span}
              size={size}
              aggregate={aggregate}
              deviceZone={deviceZone}
            />
          )}

          <section className={styles['overlaySetup']}>
            <h2>Compare tracks</h2>
            <ul className={styles['trackChoices']}>
              {tracks.map((track) => {
                const chosen = overlayIds.includes(track.id)
                const comparable = isQuantitative(track.kind)
                return (
                  <li key={track.id}>
                    <label className={comparable ? '' : styles['unavailable']}>
                      <input
                        type="checkbox"
                        checked={chosen}
                        disabled={!comparable}
                        onChange={() => {
                          setOverlayIds(
                            chosen
                              ? overlayIds.filter((id) => id !== track.id)
                              : [...overlayIds, track.id],
                          )
                        }}
                      />
                      <span
                        className={styles['swatch']}
                        style={{ background: trackColour(track.id) }}
                      />
                      {track.name}
                      {/* The reason, shown rather than left to be guessed. */}
                      {!comparable && (
                        <span className={styles['why']}>options have no order</span>
                      )}
                    </label>
                  </li>
                )
              })}
            </ul>

            <div className={styles['savedRow']}>
              <Button
                disabled={overlayIds.length < 2}
                onClick={() => {
                  void save(overlayIds)
                }}
              >
                Save this comparison
              </Button>
            </div>

            {saved.length > 0 && (
              <ul className={styles['saved']}>
                {saved.map((combination) => {
                  const named = combination.trackIds
                    .map((id) => tracks.find((track) => track.id === id)?.name ?? '?')
                    .join(' + ')
                  return (
                    <li key={combination.id}>
                      {/*
                        A saved combination is a set of tracks, not a set of
                        tracks and a range — so recalling it applies to whatever
                        range is currently selected (T-404).
                      */}
                      <Button
                        onClick={() => {
                          setOverlayIds([...combination.trackIds])
                        }}
                      >
                        {named}
                      </Button>
                      {/* Named, not counted: two saved pairs would otherwise
                          have the same accessible name and no way to tell
                          which one a screen reader is about to delete. */}
                      <Button
                        variant="ghost"
                        aria-label={`Forget ${named}`}
                        onClick={() => {
                          void remove(combination.id)
                        }}
                      >
                        Forget
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {overlayTracks.length > 0 && (
            <OverlayChart
              tracks={overlayTracks}
              entries={entries}
              span={span}
              size={size}
              aggregate={aggregate}
              deviceZone={deviceZone}
            />
          )}
        </>
      )}
    </>
  )
}
